import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCreateSaleMutation, useAddSupplyToSaleMutation } from '../../../../../../entities/sales/sales.api';
import { selectUser } from '../../../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../../../store/system/system.action';
import { SALE_STATUSES } from '../../../../../../utils/sales/sales.constants';
import { calculateNetProfit, calculateFeeBasis } from '../../../../../../utils/sales/salesMath';
import { toDateInputValue, fromDateInputValue } from '../../../../../../utils/sales/saleDates';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../../../../../utils/firebase/firebase.utils';

// styles
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  CreateButton,
  CancelButton,
  FormRow,
  FormGroup,
  ErrorMessage
} from './CreateSaleModal.styles';



// components
import InputBar from '../../../../../../components/input-bar/InputBar';
import InputTextfield from '../../../../../../components/input-textfield/InputTextfield';
import InputDropdown from '../../../../../../components/input-dropdown/InputDropdown';
import CriticalMessage from '../../../../../../components/system/critical-message/CriticalMessage';
import { PLATFORM_DATA, PLATFORM_OPTIONS } from '../../../../../../data/platform-data/platform-data';

const FIELD_KEY_MAP = {
  orderTotal: 'price',
  shipping: 'shippingCost',
  transactionFee: 'transactionFee',
  salesTax: 'salesTax'
};
export default function CreateSaleModal({ isOpen, onClose, onSuccess, initialData }) {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [createSale, { isLoading }] = useCreateSaleMutation();
  const [addSupplyToSale] = useAddSupplyToSaleMutation();
  const [isAutoFeeEnabled, setIsAutoFeeEnabled] = useState(true);

  const mapFieldKeyToFormKey = useCallback(
    (fieldKey) => FIELD_KEY_MAP[fieldKey] || fieldKey,
    []
  );

  const normalizePlatformKey = (value) => {
    if (!value) return '';
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    platform: initialData?.platform || '',
    price: initialData?.price || '',
    shippingCost: initialData?.shippingCost || '',
    transactionFee: initialData?.transactionFee || '',
    salesTax: initialData?.salesTax || '',
    notes: initialData?.notes || '',
    saleDate: toDateInputValue(initialData?.saleDate)
  });

  const [errors, setErrors] = useState({});
  const [inventoryItems, setInventoryItems] = useState([]);
  const [isInventoryLoading, setIsInventoryLoading] = useState(true);
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [selectedInventoryQty, setSelectedInventoryQty] = useState(1);
  const [selectedSupplies, setSelectedSupplies] = useState([]);

  const lastInitialDataRef = useRef(initialData);
  const wasOpenRef = useRef(false);

  // Update form data when initialData changes (e.g., when modal opens with new data)
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    const shouldReset = !wasOpenRef.current || lastInitialDataRef.current !== initialData;
    if (shouldReset) {
      const safeInitialData = initialData || {};
      setFormData({
        title: safeInitialData.title || '',
        platform: safeInitialData.platform || '',
        price: safeInitialData.price || '',
        shippingCost: safeInitialData.shippingCost || '',
        transactionFee: safeInitialData.transactionFee || '',
        salesTax: safeInitialData.salesTax || '',
        notes: safeInitialData.notes || '',
        saleDate: toDateInputValue(safeInitialData.saleDate)
      });
    }
    wasOpenRef.current = true;
    lastInitialDataRef.current = initialData;
  }, [isOpen, initialData]);

  const activePlatformKey = useMemo(() => {
    const normalized = normalizePlatformKey(formData.platform);
    return normalized || 'default';
  }, [formData.platform]);

  const activePlatformConfig = useMemo(() => {
    if (activePlatformKey === 'ebay') {
      return PLATFORM_DATA.ebay;
    }
    return PLATFORM_DATA.default || null;
  }, [activePlatformKey]);

  const activeTransactionFields = useMemo(() => {
    return activePlatformConfig?.transactionDetails?.transactionFields || [];
  }, [activePlatformConfig]);

  const getFieldValue = (fieldKey) => {
    const formKey = mapFieldKeyToFormKey(fieldKey);
    return formData[formKey] ?? '';
  };

  useEffect(() => {
    if (!isOpen) return;
    setIsAutoFeeEnabled(true);
  }, [activePlatformKey, isOpen]);

  const selectedInventoryItem = useMemo(() => {
    return inventoryItems.find(item => item.id === selectedInventoryId) || null;
  }, [inventoryItems, selectedInventoryId]);

  const suppliesTotal = useMemo(() => {
    return selectedSupplies.reduce((sum, supply) => sum + (parseFloat(supply.totalCost) || 0), 0);
  }, [selectedSupplies]);

  useEffect(() => {
    if (!isOpen || !user?.uid) {
      setInventoryItems([]);
      setIsInventoryLoading(false);
      return;
    }

    setIsInventoryLoading(true);
    const inventoryRef = collection(db, `users/${user.uid}/inventory`);
    const inventoryQuery = query(inventoryRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      inventoryQuery,
      (snapshot) => {
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInventoryItems(items);
        setIsInventoryLoading(false);
      },
      () => {
        setIsInventoryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isOpen, user?.uid]);

  useEffect(() => {
    if (selectedInventoryId) {
      setSelectedInventoryQty(1);
    }
  }, [selectedInventoryId]);

  const handleClose = () => {
    setFormData({
      title: '',
      platform: '',
      price: '',
      shippingCost: '',
      transactionFee: '',
      salesTax: '',
      notes: '',
      saleDate: ''
    });
    setSelectedInventoryId('');
    setSelectedInventoryQty(1);
    setSelectedSupplies([]);
    setErrors({});
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.platform.trim()) {
      newErrors.platform = 'Platform is required';
    }

    activeTransactionFields.forEach((field) => {
      const formKey = mapFieldKeyToFormKey(field.key);
      const value = formData[formKey];
      const isNumeric = ['currency', 'number'].includes(field.type);

      if (field.required) {
        if (isNumeric) {
          const numericValue = parseFloat(value);
          if (isNaN(numericValue) || numericValue < 0) {
            newErrors[formKey] = `Valid ${field.label} is required`;
          }
        } else if (!value || !value.trim()) {
          newErrors[formKey] = `${field.label} is required`;
        }
      } else if (isNumeric && value) {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue < 0) {
          newErrors[formKey] = 'Must be a valid number';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!user?.uid) {
      setErrors({ general: 'You must be logged in to create a sale' });
      return;
    }

    try {
      const suppliesTotalRounded = Math.round(suppliesTotal * 100) / 100;
      const saleData = {
        status: SALE_STATUSES.SOLD,
        title: formData.title.trim() || '',
        platform: formData.platform.trim(),
        price: parseFloat(formData.price) || 0,
        shippingCost: parseFloat(formData.shippingCost) || 0,
        transactionFee: parseFloat(formData.transactionFee) || 0,
        packingCost: suppliesTotalRounded,
        salesTax: parseFloat(formData.salesTax) || 0,
        notes: formData.notes.trim() || '',
        saleDate: fromDateInputValue(formData.saleDate)
      };

      const result = await createSale({
        uid: user.uid,
        saleData
      }).unwrap();

      if (selectedSupplies.length > 0) {
        try {
          await Promise.all(
            selectedSupplies.map((supply) => addSupplyToSale({
              uid: user.uid,
              saleId: result.saleId,
              supplyData: {
                inventoryItemId: supply.inventoryItemId,
                qtyUsed: supply.qtyUsed
              }
            }).unwrap())
          );
        } catch (error) {
          dispatch(setSystemNotification('Sale created, but supplies failed to save.'));
        }
      }

      handleClose();
      onSuccess?.(result);
    } catch (error) {
      setErrors({ general: error.message || 'Failed to create sale' });
    }
  };

  const sanitizeNumericInput = (value) => {
    // Remove all characters except digits and decimal point (including $)
    // Allow only one decimal point
    const cleaned = value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      // If more than one decimal point, keep only the first one
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  const roundToNearestPenny = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    // Round to 2 decimal places (nearest penny)
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  const handleNumericBlur = (field, value) => {
    const rounded = roundToNearestPenny(value);
    if (rounded !== value) {
      handleFieldChange(field, rounded);
    }
  };

  const handleFieldChange = useCallback((field, value, options = {}) => {
    const numericFieldKeys = new Set(
      activeTransactionFields
        .filter(fieldConfig => ['currency', 'number'].includes(fieldConfig.type))
        .map(fieldConfig => mapFieldKeyToFormKey(fieldConfig.key))
    );

    let sanitizedValue = value;
    if (numericFieldKeys.has(field)) {
      sanitizedValue = sanitizeNumericInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));

    if (activePlatformKey === 'ebay' && field === 'transactionFee' && !options.isAuto) {
      setIsAutoFeeEnabled(false);
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [activeTransactionFields, errors, activePlatformKey, mapFieldKeyToFormKey]);

  const calculateFinalValueFee = (orderTotal, feeConfig) => {
    if (!feeConfig || isNaN(orderTotal) || orderTotal <= 0) {
      return 0;
    }

    const tiers = Array.isArray(feeConfig.tiers) ? feeConfig.tiers : [];
    const percentTiers = tiers.filter(tier => typeof tier.upTo === 'number').sort((a, b) => a.upTo - b.upTo);
    const overTier = tiers.find(tier => typeof tier.over === 'number');

    let percentFee = 0;
    let lastCap = 0;

    percentTiers.forEach((tier) => {
      const tierMax = tier.upTo;
      if (orderTotal <= lastCap) return;
      const amountInTier = Math.min(orderTotal, tierMax) - lastCap;
      if (amountInTier > 0) {
        percentFee += amountInTier * (tier.ratePercent / 100);
        lastCap = tierMax;
      }
    });

    if (overTier && orderTotal > overTier.over) {
      percentFee += (orderTotal - overTier.over) * (overTier.ratePercent / 100);
    }

    let perOrderFee = 0;
    const perOrderFees = Array.isArray(feeConfig.perOrderFees) ? feeConfig.perOrderFees : [];
    const upToFee = perOrderFees.find(fee => typeof fee.upTo === 'number' && orderTotal <= fee.upTo);
    if (upToFee) {
      perOrderFee = upToFee.fee || 0;
    } else {
      const overFee = perOrderFees.find(fee => typeof fee.over === 'number' && orderTotal > fee.over);
      perOrderFee = overFee ? (overFee.fee || 0) : 0;
    }

    const totalFee = percentFee + perOrderFee;
    return Math.round(totalFee * 100) / 100;
  };

  useEffect(() => {
    if (!isOpen) return;
    if (activePlatformKey !== 'ebay') return;
    if (!activePlatformConfig?.finalValueFees) return;
    if (!isAutoFeeEnabled) return;

    // The fee is charged on the order total, which already includes shipping and tax.
    const orderTotalValue = calculateFeeBasis({ price: formData.price });
    const computedFee = calculateFinalValueFee(orderTotalValue, activePlatformConfig.finalValueFees);
    const formattedFee = computedFee ? computedFee.toFixed(2) : '0.00';

    if (formattedFee !== formData.transactionFee) {
      handleFieldChange('transactionFee', formattedFee, { isAuto: true });
    }
  }, [
    activePlatformConfig,
    activePlatformKey,
    formData.price,
    formData.transactionFee,
    handleFieldChange,
    isAutoFeeEnabled,
    isOpen
  ]);

  const handleInventoryQtyChange = (delta) => {
    if (!selectedInventoryItem) return;
    const maxQty = Math.max(1, parseInt(selectedInventoryItem.quantity, 10) || 1);
    setSelectedInventoryQty(prev => {
      const next = prev + delta;
      return Math.min(maxQty, Math.max(1, next));
    });
  };

  const handleAddSupply = () => {
    if (!selectedInventoryItem) return;

    const inventoryQty = parseInt(selectedInventoryItem.quantity, 10) || 0;
    const existing = selectedSupplies.find(supply => supply.inventoryItemId === selectedInventoryItem.id);
    const existingQty = existing ? existing.qtyUsed : 0;
    const availableQty = Math.max(0, inventoryQty - existingQty);

    if (selectedInventoryQty > availableQty) {
      setErrors(prev => ({
        ...prev,
        supplies: `Only ${availableQty} available in inventory.`
      }));
      return;
    }

    const totalCost = parseFloat(selectedInventoryItem.totalCost) || 0;
    const unitCost = inventoryQty > 0 ? totalCost / inventoryQty : 0;
    const updatedQty = existingQty + selectedInventoryQty;
    const updatedTotal = Math.round(unitCost * updatedQty * 100) / 100;

    setSelectedSupplies(prev => {
      if (existing) {
        return prev.map(supply => (
          supply.inventoryItemId === selectedInventoryItem.id
            ? { ...supply, qtyUsed: updatedQty, unitCost, totalCost: updatedTotal }
            : supply
        ));
      }
      return [
        ...prev,
        {
          inventoryItemId: selectedInventoryItem.id,
          itemName: selectedInventoryItem.itemName || '',
          qtyUsed: selectedInventoryQty,
          unitCost,
          totalCost: Math.round(unitCost * selectedInventoryQty * 100) / 100
        }
      ];
    });

    setSelectedInventoryQty(1);
    setErrors(prev => ({ ...prev, supplies: '' }));
  };

  const handleSupplyQtyUpdate = (inventoryItemId, delta) => {
    const inventoryItem = inventoryItems.find(item => item.id === inventoryItemId);
    if (!inventoryItem) return;

    const inventoryQty = parseInt(inventoryItem.quantity, 10) || 0;
    const unitCost = inventoryQty > 0 ? (parseFloat(inventoryItem.totalCost) || 0) / inventoryQty : 0;

    setSelectedSupplies(prev => (
      prev.map(supply => {
        if (supply.inventoryItemId !== inventoryItemId) return supply;
        const nextQty = Math.max(1, Math.min(inventoryQty, supply.qtyUsed + delta));
        return {
          ...supply,
          qtyUsed: nextQty,
          unitCost,
          totalCost: Math.round(unitCost * nextQty * 100) / 100
        };
      })
    ));
  };

  const handleRemoveSupply = (inventoryItemId) => {
    setSelectedSupplies(prev => prev.filter(supply => supply.inventoryItemId !== inventoryItemId));
  };

  const calculateProfit = () => calculateNetProfit({
    price: formData.price,
    salesTax: formData.salesTax,
    shippingCost: formData.shippingCost,
    transactionFee: formData.transactionFee,
    packingCost: suppliesTotal
  });

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const transactionFieldRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < activeTransactionFields.length; i += 2) {
      rows.push(activeTransactionFields.slice(i, i + 2));
    }
    return rows;
  }, [activeTransactionFields]);

  const isSubmitDisabled = useMemo(() => {
    if (!formData.platform.trim()) return true;
    return activeTransactionFields.some((field) => {
      if (!field.required) return false;
      const formKey = mapFieldKeyToFormKey(field.key);
      const value = formData[formKey];
      if (['currency', 'number'].includes(field.type)) {
        const numericValue = parseFloat(value);
        return isNaN(numericValue) || numericValue < 0;
      }
      return !value || !value.trim();
    });
  }, [activeTransactionFields, formData, mapFieldKeyToFormKey]);

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='widget-bg'>
        <ModalHeader>
          <ModalTitle>Add New Sale</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <InputBar
                  type="text"
                  id="title"
                  label="Title"
                  value={formData.title}
                  updateValue={(value) => handleFieldChange('title', value)}
                />
                {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <InputDropdown
                  id="platform"
                  label="Platform *"
                  value={formData.platform}
                  onChange={(value) => handleFieldChange('platform', value)}
                  options={PLATFORM_OPTIONS}
                  placeholder="Select platform"
                />
                {errors.platform && <ErrorMessage>{errors.platform}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <InputBar
                  type="date"
                  id="saleDate"
                  label="Sold Date"
                  value={formData.saleDate}
                  updateValue={(value) => handleFieldChange('saleDate', value)}
                />
              </FormGroup>
            </FormRow>

            {transactionFieldRows.map((row, rowIndex) => (
              <FormRow key={`transaction-row-${rowIndex}`}>
                {row.map((field) => {
                  const formKey = mapFieldKeyToFormKey(field.key);
                  const isCurrency = field.type === 'currency';
                  const label = field.required ? `${field.label} *` : field.label;

                  return (
                    <FormGroup key={field.key}>
                      <InputBar
                        type={isCurrency ? 'text' : (field.type || 'text')}
                        inputMode={isCurrency ? 'decimal' : undefined}
                        id={formKey}
                        label={label}
                        prefix={isCurrency ? '$' : undefined}
                        value={getFieldValue(field.key)}
                        updateValue={(value) => handleFieldChange(mapFieldKeyToFormKey(field.key), value)}
                        onBlur={isCurrency ? () => handleNumericBlur(formKey, formData[formKey]) : undefined}
                      />
                      {errors[formKey] && <ErrorMessage>{errors[formKey]}</ErrorMessage>}
                    </FormGroup>
                  );
                })}
              </FormRow>
            ))}

            <FormGroup>
              <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px', marginBottom: '6px' }}>
                Supplies Cost (from Inventory)
              </div>
              <InputDropdown
                id="inventory-item"
                label="Inventory Item"
                value={selectedInventoryId}
                onChange={(value) => setSelectedInventoryId(value)}
                placeholder={isInventoryLoading ? 'Loading inventory...' : 'Select inventory item'}
              >
                {inventoryItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.itemName}
                  </option>
                ))}
              </InputDropdown>

              {selectedInventoryItem && (
                <div style={{
                  marginTop: '10px',
                  padding: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.04)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <button type="button" onClick={() => handleInventoryQtyChange(-1)}>−</button>
                    <span style={{ minWidth: '24px', textAlign: 'center' }}>{selectedInventoryQty}</span>
                    <button type="button" onClick={() => handleInventoryQtyChange(1)}>+</button>
                    <button
                      type="button"
                      onClick={handleAddSupply}
                      style={{ marginLeft: 'auto' }}
                    >
                      Add Item(s)
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                    <span>Cost / Unit</span>
                    <span>
                      {formatCurrency(
                        (parseFloat(selectedInventoryItem.totalCost) || 0) /
                          Math.max(1, parseInt(selectedInventoryItem.quantity, 10) || 1)
                      )}
                    </span>
                  </div>
                </div>
              )}

              {selectedSupplies.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedSupplies.map((supply) => (
                    <div
                      key={supply.inventoryItemId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ fontSize: '12px' }}>{supply.itemName || 'Supply'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button type="button" onClick={() => handleSupplyQtyUpdate(supply.inventoryItemId, -1)}>−</button>
                        <span style={{ minWidth: '24px', textAlign: 'center' }}>{supply.qtyUsed}</span>
                        <button type="button" onClick={() => handleSupplyQtyUpdate(supply.inventoryItemId, 1)}>+</button>
                        <span style={{ fontSize: '12px' }}>{formatCurrency(supply.totalCost)}</span>
                        <button type="button" onClick={() => handleRemoveSupply(supply.inventoryItemId)}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '10px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                Supplies Cost: {formatCurrency(suppliesTotal)}
              </div>
              {errors.supplies && <ErrorMessage>{errors.supplies}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <InputTextfield
                id="notes"
                label="Notes"
                value={formData.notes}
                updateValue={(value) => handleFieldChange('notes', value)}
                rows={3}
                maxLength={500}
              />
            </FormGroup>

            <FormGroup>
              {(() => {
                const profit = calculateProfit();
                const profitColor = profit >= 0 ? '#4CAF50' : '#F44336';
                return (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      Profit
                    </span>
                    <span style={{
                      color: profitColor,
                      fontSize: '18px',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(profit)}
                    </span>
                  </div>
                );
              })()}
            </FormGroup>

            {errors.general && (
              <ErrorMessage>
                <CriticalMessage text={errors.general} color='red' />
              </ErrorMessage>
            )}
          </form>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <CancelButton onClick={handleClose} disabled={isLoading}>
              Cancel
            </CancelButton>
            <CreateButton 
              onClick={handleSubmit}
              disabled={isLoading || isSubmitDisabled}
            >
              {isLoading ? 'Adding...' : 'Add Sale'}
            </CreateButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}

